import React, { useState, useEffect } from 'react';
import { Link, useParams, Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home, ChevronRight, DownloadIcon, FileText,
  Calendar, Tag, ArrowLeft, Loader, HardDrive, Info,
  ExternalLink, FolderOpen, ShieldCheck
} from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';
import { useTheme } from '../context/ThemeContext';
import { fetchUnduhanById, fetchUnduhanBySlug, fetchUnduhan, formatTanggalIndonesia } from '../utils/api';
import SEO from '../components/SEO';

const CAT_LABELS = {
  Formulir: 'Formulir',
  Panduan: 'Panduan',
  Kalender: 'Kalender',
  Laporan_Regulasi: 'Laporan & Regulasi',
  'Laporan & Regulasi': 'Laporan & Regulasi',
  akreditasi: 'Akreditasi',
  sertifikat: 'Akreditasi',
};
const getCatLabel = (val) => CAT_LABELS[val] || (val ? val.charAt(0).toUpperCase() + val.slice(1) : 'Umum');

const ICON_COLORS = {
  pdf:     { bg: 'bg-red-50 dark:bg-red-900/20',     text: 'text-red-500',    label: 'PDF' },
  doc:     { bg: 'bg-blue-50 dark:bg-blue-900/20',   text: 'text-blue-500',   label: 'DOCX' },
  docx:    { bg: 'bg-blue-50 dark:bg-blue-900/20',   text: 'text-blue-500',   label: 'DOCX' },
  xls:     { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-500',  label: 'XLSX' },
  xlsx:    { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-500',  label: 'XLSX' },
  default: { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-500', label: 'FILE' },
};
const getIconStyle = (tipe) => {
  const t = (tipe || '').toLowerCase();
  if (t.includes('pdf')) return ICON_COLORS.pdf;
  if (t.includes('doc')) return ICON_COLORS.doc;
  if (t.includes('xls')) return ICON_COLORS.xls;
  return ICON_COLORS.default;
};

// UUID v4 pattern — param is an id, not a slug
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const DownloadDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const [file, setFile]         = useState(null);
  const [related, setRelated]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileError, setTurnstileError] = useState('');

  useEffect(() => {
    setLoading(true);
    // Reset turnstile on page change
    setTurnstileToken(null);
    setTurnstileError('');

    const isUUID = UUID_RE.test(slug);
    const resolve = isUUID
      ? fetchUnduhanById(slug)
      : fetchUnduhanBySlug(slug.replace(/\s+/g, '-'));

    resolve.then((data) => {
      if (!data || data.status === 'draft') {
        setNotFound(true);
        setLoading(false);
        return;
      }
      // If accessed by UUID but a slug exists, redirect to slug URL
      if (isUUID && data.slug) {
        navigate(`/download/${data.slug.replace(/\s+/g, '-')}`, { replace: true });
        return;
      }
      setFile(data);
      fetchUnduhan().then((all) => {
        setRelated(
          all.filter(f => f.id !== data.id && f.kategori === data.kategori).slice(0, 5)
        );
      });
      setLoading(false);
    }).catch(() => { setNotFound(true); setLoading(false); });
  }, [slug, navigate]);

  const handleDownload = () => {
    if (!turnstileToken) return;
    const url = file.berkasUrl || '';
    if (url) window.open(url, '_blank');
    else alert('Link download belum tersedia / sedang diproses oleh server.');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F7FF] dark:bg-[#121020]">
        <Loader className="animate-spin text-[#7B2FBE]" size={32} />
      </div>
    );
  }

  if (notFound) return <Navigate to="/download" replace />;

  const icon     = getIconStyle(file.tipe_ekstensi);
  const catLabel = getCatLabel(file.kategori);

  return (
    <div data-testid="download-detail-page">
      <SEO
        title={file.nama_file}
        description={`Unduh ${file.nama_file} — ${catLabel}. Dokumen resmi STIKES Harapan Ibu Jambi.`}
      />

      {/* Hero */}
      <div className="page-hero pt-[70px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-white/70 text-sm mb-4 flex-wrap">
              <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
                <Home size={14} /> Beranda
              </Link>
              <ChevronRight size={14} />
              <Link to="/download" className="hover:text-white transition-colors">Download File</Link>
              <ChevronRight size={14} />
              <span className="text-white truncate max-w-[180px] sm:max-w-xs">{file.nama_file}</span>
            </div>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-white/20 text-white">{catLabel}</span>
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-white/20 text-white uppercase">
                {file.tipe_ekstensi || 'FILE'}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white font-heading leading-tight max-w-3xl">
              {file.nama_file}
            </h1>
          </motion.div>
        </div>
      </div>

      <div className="bg-[#F8F7FF] dark:bg-[#121020]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* LEFT: Detail card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-2 space-y-6"
            >
              {/* File Preview Card */}
              <div className="bg-white dark:bg-[#1A182E] rounded-3xl p-8 border border-gray-100 dark:border-gray-800 shadow-sm">

                {/* File header */}
                <div className="flex items-start gap-6">
                  <div className={`w-20 h-20 rounded-2xl ${icon.bg} ${icon.text} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                    <FileText size={38} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold text-[#1A1A2E] dark:text-white font-heading leading-snug mb-3">
                      {file.nama_file}
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-lg border border-current/20 uppercase ${icon.text} bg-white dark:bg-[#1A182E]`}>
                        {file.tipe_ekstensi || 'FILE'}
                      </span>
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-purple-50 dark:bg-purple-900/30 text-[#7B2FBE] dark:text-purple-300 flex items-center gap-1">
                        <Tag size={11} /> {catLabel}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Metadata grid */}
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: <Tag size={16} />, label: 'Kategori', value: catLabel },
                    { icon: <FileText size={16} />, label: 'Format File', value: (file.tipe_ekstensi || 'FILE').toUpperCase() },
                    file.ukuran_file && { icon: <HardDrive size={16} />, label: 'Ukuran File', value: file.ukuran_file },
                    (file.tanggal_publikasi || file.date_created) && {
                      icon: <Calendar size={16} />,
                      label: 'Tanggal Publikasi',
                      value: formatTanggalIndonesia(file.tanggal_publikasi || file.date_created),
                    },
                  ].filter(Boolean).map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 rounded-2xl bg-gray-50 dark:bg-[#121020] border border-gray-100 dark:border-gray-800">
                      <span className="text-[#7B2FBE] dark:text-purple-400 flex-shrink-0 mt-0.5">{item.icon}</span>
                      <div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 font-medium mb-0.5">{item.label}</p>
                        <p className="text-sm font-semibold text-[#1A1A2E] dark:text-white">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {file.deskripsi && (
                  <div className="mt-6 p-5 rounded-2xl bg-gray-50 dark:bg-[#121020] border border-gray-100 dark:border-gray-800">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Deskripsi</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{file.deskripsi}</p>
                  </div>
                )}

                {/* Turnstile + Download CTA */}
                <div className="mt-8 pt-7 border-t border-gray-100 dark:border-gray-800">
                  <div className="flex flex-col items-start gap-4">

                    {/* Turnstile widget */}
                    <div className="flex flex-col gap-2">
                      <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                        <ShieldCheck size={13} /> Verifikasi Keamanan
                      </p>
                      <Turnstile
                        siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'}
                        theme={isDark ? 'dark' : 'light'}
                        onSuccess={(token) => { setTurnstileToken(token); setTurnstileError(''); }}
                        onError={() => { setTurnstileToken(null); setTurnstileError('Gagal memuat verifikasi. Coba muat ulang halaman.'); }}
                        onExpire={() => { setTurnstileToken(null); setTurnstileError('Verifikasi kedaluwarsa. Silakan verifikasi ulang.'); }}
                      />
                      {turnstileError && (
                        <p className="text-xs text-red-500 font-medium">{turnstileError}</p>
                      )}
                      {!turnstileToken && !turnstileError && (
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          Selesaikan verifikasi di atas untuk mengaktifkan tombol unduh.
                        </p>
                      )}
                    </div>

                    {/* Download buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 w-full">
                      <button
                        onClick={handleDownload}
                        disabled={!turnstileToken}
                        className={`flex-1 flex items-center justify-center gap-2.5 py-4 rounded-2xl font-bold text-sm
                          transition-all duration-300 shadow-xl
                          ${turnstileToken
                            ? 'bg-gradient-to-r from-[#7B2FBE] to-[#9333EA] text-white hover:opacity-90 hover:scale-[1.01] active:scale-[0.98] shadow-purple-500/20 cursor-pointer'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 shadow-none cursor-not-allowed'
                          }`}
                      >
                        <DownloadIcon size={18} />
                        {turnstileToken ? 'Unduh File Sekarang' : 'Selesaikan Verifikasi Dulu'}
                      </button>

                      {file.sumber_file === 'Link Eksternal' && file.link_eksternal && turnstileToken && (
                        <a
                          href={file.link_eksternal}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 px-5 py-4 rounded-2xl border-2 border-[#7B2FBE]/30 text-[#7B2FBE] dark:text-purple-400 font-semibold text-sm hover:bg-[#7B2FBE]/5 transition-all"
                        >
                          <ExternalLink size={16} /> Buka Link
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Info note */}
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800/40 rounded-xl p-5 flex items-start gap-4">
                <Info size={18} className="text-[#7B2FBE] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  File ini tersedia untuk mahasiswa dan civitas akademika STIKES HI Jambi.
                  Jika ada kendala unduhan, silakan hubungi Bagian Akademik atau Unit Informasi &amp; Teknologi.
                </p>
              </div>
            </motion.div>

            {/* RIGHT: Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="space-y-6"
            >
              {/* Back button */}
              <Link
                to={`/download${file.kategori ? `?kategori=${encodeURIComponent(file.kategori)}` : ''}`}
                className="flex items-center gap-2 text-sm text-[#7B2FBE] dark:text-purple-400 font-semibold hover:underline"
              >
                <ArrowLeft size={16} /> Kembali ke Daftar {catLabel}
              </Link>

              {/* Related files */}
              {related.length > 0 && (
                <div className="bg-white dark:bg-[#1A182E] rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
                    <FolderOpen size={16} className="text-[#7B2FBE]" />
                    <h3 className="font-bold text-[#1A1A2E] dark:text-white text-sm">File {catLabel} Lainnya</h3>
                  </div>
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {related.map((rel) => {
                      const relIcon = getIconStyle(rel.tipe_ekstensi);
                      return (
                        <Link
                          key={rel.id}
                          to={`/download/${rel.slug ? rel.slug.replace(/\s+/g, '-') : rel.id}`}
                          className="flex items-center gap-3 px-5 py-4 hover:bg-purple-50/50 dark:hover:bg-purple-900/10 transition-colors group"
                        >
                          <div className={`w-9 h-9 rounded-xl ${relIcon.bg} ${relIcon.text} flex items-center justify-center flex-shrink-0`}>
                            <FileText size={16} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[#1A1A2E] dark:text-white group-hover:text-[#7B2FBE] transition-colors line-clamp-2 leading-snug">
                              {rel.nama_file}
                            </p>
                            {rel.tanggal_publikasi && (
                              <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                                <Calendar size={10} /> {formatTanggalIndonesia(rel.tanggal_publikasi)}
                              </p>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Share box */}
              <div className="bg-white dark:bg-[#1A182E] rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-5">
                <h3 className="font-bold text-[#1A1A2E] dark:text-white text-sm mb-3">Bagikan File Ini</h3>
                <div className="flex items-center gap-2 bg-gray-50 dark:bg-[#121020] rounded-xl px-3 py-2.5 border border-gray-200 dark:border-gray-700">
                  <input
                    readOnly
                    value={window.location.href}
                    className="flex-1 text-xs text-gray-500 dark:text-gray-400 bg-transparent outline-none truncate"
                  />
                  <button
                    onClick={() => navigator.clipboard.writeText(window.location.href)}
                    className="text-xs font-semibold text-[#7B2FBE] dark:text-purple-400 hover:underline flex-shrink-0"
                  >
                    Salin
                  </button>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadDetail;
